<template>
    <TprmModal
        additional-class-name="UploadContractModal"
        title="Upload Contract"
        :subtitle="uploadContractSubtitle"
        :open="open"
        size="md"
        allow-close
        @close="$emit('close')"
    >
        <div class="UploadContractModal__content ">
            <NewFileListInput
                id="upload-contract-input"
                :value="submissionFiles"
                :multiple="false"
                :disabled="submitting"
                @input="onFilesChange"
            />
            <div v-if="hasInvalidFiles" class="UploadContractModal__validationError">
                Please remove invalid files before uploading.
            </div>
        </div>

        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                :disabled="submitting"
                @click="close"
            >
                Cancel
            </NewButton>
            <NewButton
                color="primary"
                :loading="submitting"
                :disabled="!canUpload"
                @click="upload"
            >
                <EidIcon :icon-src="faUploadContract" />
                {{ uploadButtonLabel }}
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
    .UploadContractModal {
        &__content {
            display: flex;
            flex-direction: column;
            gap: 14px;
        }

        &__validationError {
            color: #be3232;
            font-size: 13px;
            line-height: 1.35;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import NewFileListInput from '@/components/new-file-list-input/NewFileListInput.vue';
    import { NewFileListInputFile } from '@/components/new-file-list-input/types';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    export default defineComponent({
        name: 'UploadContractModal',
        components: {
            NewFileListInput,
            NewButton,
            TprmModal,
            EidIcon,
        },
        props: {
            open: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            submitting: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'close', 'upload' ],
        data() {
            return {
                faUploadContract: faArrowUpFromBracket,
                submissionFiles: [] as NewFileListInputFile[],
                uploadContractSubtitle: 'Upload a contract to be extracted for requirements, automatically create a risk profile and assign to an entity.',
            };
        },
        computed: {
            hasInvalidFiles(): boolean {
                return this.submissionFiles.some((submissionFile) => Boolean(submissionFile.error));
            },
            validFiles(): File[] {
                return this.submissionFiles
                    .filter((submissionFile) => !submissionFile.error)
                    .map((submissionFile) => submissionFile.file);
            },
            canUpload(): boolean {
                return this.validFiles.length > 0 && !this.hasInvalidFiles;
            },
            uploadButtonLabel(): string {
                return 'Upload Contract';
            },
        },
        watch: {
            open(nextOpen: boolean) {
                if (!nextOpen) {
                    this.reset();
                }
            },
        },
        methods: {
            reset(): void {
                this.submissionFiles = [];
            },
            close(): void {
                this.$emit('close');
            },
            onFilesChange(files: NewFileListInputFile[]): void {
                this.submissionFiles = files.slice(0, 1);
            },
            upload(): void {
                const [ file ] = this.validFiles;
                if (!file || this.hasInvalidFiles || this.submitting) {
                    return;
                }
                this.$emit('upload', [ file ]);
            },
        },
    });
</script>
